import { Alert, Button, Drawer, TextField, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useMutation } from "@tanstack/react-query";
import InputField from "components/Input/InputField";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createPartnerLead, fetchPartnerLead, updatePartnerLead } from "../../api/endpoints/leads";
import { canEditLead } from "./leadHelpers";

const EMPTY = {
  name: "",
  email: "",
  number: "",
  address: "",
  nationality: "",
  gender: "",
  dob: "",
  passportNumber: "",
  interestedServices: "",
  comments: "",
};

/**
 * The intake fields a partner may fill. Disabled wholesale once the lead is with the office;
 * a rejection reason shows as an alert so the partner knows what to fix.
 */
const LeadEditorDrawer = ({ open, lead, onClose, onSaved }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (lead?.id) {
      fetchPartnerLead(lead.id)
        .then((loaded) => {
          setDetail(loaded);
          setForm({
            name: loaded.name || "",
            email: loaded.email || "",
            number: loaded.number || "",
            address: loaded.address || "",
            nationality: loaded.nationality || "",
            gender: loaded.gender || "",
            dob: loaded.dob || "",
            passportNumber: loaded.passportNumber || "",
            interestedServices: (loaded.interestedServices || []).join(", "),
            comments: loaded.comments || "",
          });
        })
        .catch(() => {
          setDetail(lead);
          setForm({
            ...EMPTY,
            name: lead.name || "",
            email: lead.email || "",
            number: lead.number || "",
          });
        });
    } else {
      setDetail(null);
      setForm(EMPTY);
    }
  }, [open, lead]);

  const locked = detail ? !canEditLead(detail) : false;

  const saveMutation = useMutation(
    (payload) => (lead?.id ? updatePartnerLead(lead.id, payload) : createPartnerLead(payload)),
    {
      onSuccess: () => {
        toast.success(lead?.id ? "Lead updated" : "Lead created");
        onSaved?.();
        onClose?.();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.errorMessage || err?.response?.data?.message || "Could not save"
        ),
    }
  );

  const patch = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error("A lead needs a name");
      return;
    }
    saveMutation.mutate({
      ...form,
      name: form.name.trim(),
      email: form.email.trim(),
      number: form.number.trim(),
      interestedServices: String(form.interestedServices || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-full sm:w-[480px] max-w-[100vw] h-full flex flex-col bg-white">
        <div className="px-4 py-3 border-b border-slate-200">
          <Typography variant="h6" className="font-semibold">
            {lead?.id ? "Edit lead" : "New client"}
          </Typography>
          {detail?.state === "REJECTED" && detail?.rejectionReason ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Returned by the office: {detail.rejectionReason}
            </Alert>
          ) : null}
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {lead?.id ? (
            <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-3">
              <p className="text-sm text-slate-700 m-0">
                These are the contact details you started the lead with. The client's full form,
                including everything they sent back, opens on its own page.
              </p>
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ mt: 1.5 }}
                onClick={() => {
                  onClose?.();
                  navigate(`/partner/leads/${lead.id}/form`);
                }}
              >
                {locked ? "View the full form" : "Review or fill the full form"}
              </Button>
            </div>
          ) : null}
          <InputField
            label="Full name"
            name="name"
            required
            value={form.name}
            onChange={patch}
            disabled={locked}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Email"
              name="email"
              type="email"
              value={form.email}
              onChange={patch}
              disabled={locked}
            />
            <InputField
              label="Phone"
              name="number"
              value={form.number}
              onChange={patch}
              disabled={locked}
            />
          </div>
          <InputField
            label="Address"
            name="address"
            value={form.address}
            onChange={patch}
            disabled={locked}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField
              label="Nationality"
              name="nationality"
              value={form.nationality}
              onChange={patch}
              disabled={locked}
            />
            <InputField
              label="Gender"
              name="gender"
              value={form.gender}
              onChange={patch}
              disabled={locked}
            />
            <InputField
              label="Date of birth (yyyy-mm-dd)"
              name="dob"
              type="date"
              value={form.dob}
              onChange={patch}
              disabled={locked}
            />
            <InputField
              label="Passport number"
              name="passportNumber"
              value={form.passportNumber}
              onChange={patch}
              disabled={locked}
            />
          </div>
          <TextField
            fullWidth
            size="small"
            label="Interested services (comma separated)"
            name="interestedServices"
            value={form.interestedServices}
            onChange={patch}
            disabled={locked}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            rows={3}
            label="Comments"
            name="comments"
            value={form.comments}
            onChange={patch}
            disabled={locked}
          />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200">
          <Button variant="outlined" onClick={onClose}>
            {locked ? "Close" : "Cancel"}
          </Button>
          {!locked ? (
            <Button variant="contained" disabled={saveMutation.isLoading} onClick={handleSave}>
              {saveMutation.isLoading ? "Saving…" : "Save lead"}
            </Button>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
};

export default LeadEditorDrawer;
